import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { personasAPI } from "@/lib/api";
import { ArrowLeft, Save, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function PersonaFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    tipo: "feligres",
    genero: "masculino",
    fecha_nacimiento: "",
    lugar_nacimiento: "",
    cedula: "",
    direccion: "",
    telefono: "",
    email: "",
    padre_nombre: "",
    madre_nombre: "",
    notas: "",
  });

  useEffect(() => {
    if (isEditing) {
      loadPersona();
    }
  }, [id]);

  const loadPersona = async () => {
    try {
      const response = await personasAPI.getOne(id);
      setFormData(response.data);
    } catch (error) {
      toast.error("Error al cargar la persona");
      navigate("/personas");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditing) {
        await personasAPI.update(id, formData);
        toast.success("Persona actualizada correctamente");
      } else {
        await personasAPI.create(formData);
        toast.success("Persona registrada correctamente");
      }
      navigate("/personas");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar la persona");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="persona-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/personas")}
          className="h-10 w-10"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
            <Users className="w-8 h-8 text-emerald-600" strokeWidth={1.5} />
            {isEditing ? "Editar Persona" : "Nueva Persona"}
          </h1>
          <p className="text-stone-600 mt-1">
            {isEditing ? "Modifica los datos del registro" : "Ingresa los datos de la nueva persona"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Datos Personales */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Datos Personales
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="apellido">Apellido *</Label>
              <Input
                id="apellido"
                name="apellido"
                value={formData.apellido}
                onChange={handleChange}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tipo">Tipo *</Label>
              <Select
                value={formData.tipo}
                onValueChange={(value) => handleSelectChange("tipo", value)}
              >
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="clerigo">Clérigo</SelectItem>
                  <SelectItem value="religioso">Religioso</SelectItem>
                  <SelectItem value="feligres">Feligrés</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="genero">Género *</Label>
              <Select
                value={formData.genero}
                onValueChange={(value) => handleSelectChange("genero", value)}
              >
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="masculino">Masculino</SelectItem>
                  <SelectItem value="femenino">Femenino</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fecha_nacimiento">Fecha de Nacimiento</Label>
              <Input
                id="fecha_nacimiento"
                name="fecha_nacimiento"
                type="date"
                value={formData.fecha_nacimiento}
                onChange={handleChange}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lugar_nacimiento">Lugar de Nacimiento</Label>
              <Input
                id="lugar_nacimiento"
                name="lugar_nacimiento"
                value={formData.lugar_nacimiento}
                onChange={handleChange}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cedula">Cédula / Identificación</Label>
              <Input
                id="cedula"
                name="cedula"
                value={formData.cedula}
                onChange={handleChange}
                className="h-11"
              />
            </div>
          </CardContent>
        </Card>

        {/* Contacto */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Información de Contacto
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="direccion">Dirección</Label>
              <Input
                id="direccion"
                name="direccion"
                value={formData.direccion}
                onChange={handleChange}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="telefono">Teléfono</Label>
              <Input
                id="telefono"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Correo Electrónico</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="h-11"
              />
            </div>
          </CardContent>
        </Card>

        {/* Familia */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Datos Familiares
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="padre_nombre">Nombre del Padre</Label>
              <Input
                id="padre_nombre"
                name="padre_nombre"
                value={formData.padre_nombre}
                onChange={handleChange}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="madre_nombre">Nombre de la Madre</Label>
              <Input
                id="madre_nombre"
                name="madre_nombre"
                value={formData.madre_nombre}
                onChange={handleChange}
                className="h-11"
              />
            </div>
          </CardContent>
        </Card>

        {/* Notas */}
        <Card className="border-stone-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-stone-800">
              Observaciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="notas">Notas</Label>
              <Textarea
                id="notas"
                name="notas"
                value={formData.notas}
                onChange={handleChange}
                rows={4}
                className="resize-none"
                placeholder="Sacramentos recibidos, ministerios, grupos, etc."
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/personas")}
            className="h-11"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="h-11 bg-rose-950 hover:bg-rose-900 text-white"
            data-testid="guardar-persona-btn"
          >
            {loading ? (
              <span className="animate-pulse">Guardando...</span>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? "Actualizar" : "Guardar"} Persona
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
